"use client";

import clsx from "clsx";
import { Monogram } from "@/components/persona/Monogram";
import { BankBadge } from "@/components/banks/BankBadge";
import { TradeDossier } from "@/components/sign/TradeDossier";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screen 7.18 — T4 reveal. Air-corridor reputation arc. Verde Carnes and
 * Al Bayt both gain a verified trade; Al Bayt's bank-partner list now
 * includes FAB. Mirrors T3Reveal's 2-column layout (reputation panes
 * + dossier on the left, time-elapsed + cumulative T1+T2+T3+T4 strip on
 * the right) but with air-modality copy and FAB fast-track LC framing.
 */
export type ReputationDelta = {
  before: Record<string, unknown>;
  after: Record<string, unknown>;
};

export function T4Reveal({
  verdeCarnes,
  alBayt,
  className,
}: {
  verdeCarnes: ReputationDelta | null;
  alBayt: ReputationDelta | null;
  className?: string;
}) {
  const t = useTranslations("t4Scenario");
  return (
    <div className={clsx("flex h-full flex-col gap-3", className)}>
      {/* Header banner — "trade closed at the speed of air" hero moment.   */}
      <div className="glass relative overflow-hidden p-4">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-state-financed to-transparent" />
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{t("revealKicker")}</p>
        <h2 className="mt-1 font-display text-xl font-semibold text-orbitra-paper">
          {t("revealTitle")}
        </h2>
        <p className="mt-1 text-[11px] text-orbitra-teal-soft">
          {t("revealSummary")}
        </p>
        <div className="mt-2 flex items-center gap-2">
          <BankBadge name="FAB" fundingPct={70} variant="edge" />
          <span className="rounded-full border border-state-financed/40 bg-state-financed/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-financed">
            {t("revealLcBadge")}
          </span>
          <span className="rounded-full border border-state-traced/40 bg-state-traced/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-traced">
            {t("revealFlightBadge")}
          </span>
        </div>
      </div>

      <div className="grid flex-1 min-h-0 grid-cols-3 gap-3">
        <div className="col-span-2 flex min-h-0 flex-col gap-3">
          <div className="grid flex-1 min-h-0 grid-cols-2 gap-3">
            <ReputationPane
              name={t("revealPaneVerdeName")}
              monogramName="Verde Carnes"
              flag="BR"
              headline={t("revealPaneHeadline")}
              before={verdeCarnes?.before ?? { verifiedTrades: 31 }}
              after={verdeCarnes?.after   ?? { verifiedTrades: 32, lifetimeVolumeUsd: 18_680_000 }}
              arc={t("revealPaneArcVerde")}
            />
            <ReputationPane
              name={t("revealPaneAlBaytName")}
              monogramName="Al Bayt"
              flag="AE"
              headline={t("revealPaneHeadline")}
              before={alBayt?.before ?? { verifiedTrades: 14 }}
              after={alBayt?.after   ?? { verifiedTrades: 15, lifetimeVolumeUsd: 6_480_000, bankPartnersList: ["FAB"] }}
              arc={t("revealPaneArcAlBayt")}
              showBankPartner
            />
          </div>
          <TradeDossier scenarioId="t4-air-cargo" />
        </div>

        <div className="flex min-h-0 flex-col gap-3">
          <div className="glass flex flex-col gap-1 px-3 py-2 text-[11px]">
            <span className="text-orbitra-paper">
              {t("revealTimeElapsedLabel")} <span className="font-mono text-state-traced">{t("revealTimeElapsedValue")}</span>
            </span>
            <span className="text-orbitra-muted">
              {t("revealPreOrbitraLabel")} <span className="font-mono">{t("revealPreOrbitraValue")}</span>
            </span>
            <span className="text-orbitra-muted">
              {t("revealFabIntegrationLabel")} <span className="font-mono">{t("revealFabIntegrationValue")}</span>
            </span>
          </div>

          {/* Cumulative T1+T2+T3+T4 strip — bumped to 4 trades, 2 modalities. */}
          <div className="glass relative flex-1 min-h-0 overflow-hidden p-3">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-state-verified via-state-traced to-state-financed opacity-60" />
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("revealCumulativeKicker")}
            </p>
            <div className="mt-2 flex flex-col gap-1.5 text-[11px]">
              <CumulativeStat label={t("revealCumTradesLabel")}    value="4" sub={t("revealCumTradesSub")} />
              <CumulativeStat label={t("revealCumCorridorsLabel")} value="2" sub={t("revealCumCorridorsSub")} />
              <CumulativeStat label={t("revealCumModalitiesLabel")} value="2" sub={t("revealCumModalitiesSub")} />
              <CumulativeStat label={t("revealCumFinancingLabel")} value="4" sub={t("revealCumFinancingSub")} />
              <CumulativeStat label={t("revealCumDisputesLabel")}  value="0" sub={t("revealCumDisputesSub")} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReputationPane({
  name,
  monogramName,
  flag,
  headline,
  before,
  after,
  arc,
  showBankPartner,
}: {
  name: string;
  monogramName: string;
  flag: string;
  headline: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  arc: string;
  showBankPartner?: boolean;
}) {
  const t = useTranslations("t4Scenario");
  return (
    <div className="glass flex min-h-0 flex-col p-4">
      <div className="flex items-center gap-3">
        <Monogram name={monogramName} flag={flag} size={36} accent="var(--state-verified)" />
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{headline}</p>
          <p className="mt-0.5 font-display text-[15px] font-semibold text-orbitra-paper">{name}</p>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        <Stat label={t("revealPaneStatVerified")}  beforeV={fmt(before.verifiedTrades)}       afterV={fmt(after.verifiedTrades)} />
        <Stat label={t("revealPaneStatVolume")}    beforeV={fmtUsd(before.lifetimeVolumeUsd)} afterV={fmtUsd(after.lifetimeVolumeUsd)} />
        <Stat label={t("revealPaneStatOnTime")}    beforeV={fmtPct(before.onTimePct)}         afterV={fmtPct(after.onTimePct)} />
        <Stat label={t("revealPaneStatBankShare")} beforeV={fmtPct(before.bankFinancedPct)}   afterV={fmtPct(after.bankFinancedPct)} />
      </div>

      {showBankPartner && (
        <div className="mt-2 flex items-center gap-2 rounded-md border border-state-financed/30 bg-state-financed/8 px-3 py-1.5 text-[11px] text-state-financed">
          <span className="font-mono text-[10px] uppercase tracking-wider">{t("revealPaneBankPartners")}</span>
          <BankBadge name="FAB" variant="compact" />
        </div>
      )}

      <div className="mt-auto rounded-md border border-state-financed/30 bg-state-financed/8 px-3 py-1.5 text-[11px] text-state-financed">
        {t("revealPaneArcLabel")} · <span className="font-mono">{arc}</span>
      </div>
    </div>
  );
}

function Stat({ label, beforeV, afterV }: { label: string; beforeV: string; afterV: string }) {
  const changed = beforeV !== afterV;
  return (
    <div className="rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <span className={clsx("font-mono text-[11px]", changed ? "text-orbitra-muted line-through" : "text-orbitra-paper")}>{beforeV}</span>
        {changed && <span className="font-mono text-[10px] text-state-financed">→</span>}
        {changed && <span className="font-display text-base font-semibold text-state-financed">{afterV}</span>}
      </div>
    </div>
  );
}

function CumulativeStat({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="font-display text-2xl font-semibold leading-none text-state-financed">{value}</p>
      <div className="flex min-w-0 flex-col">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-paper">{label}</p>
        <p className="truncate text-[9px] text-orbitra-muted">{sub}</p>
      </div>
    </div>
  );
}

function fmt(v: unknown): string { return v === undefined || v === null ? "—" : String(v); }
function fmtUsd(v: unknown): string {
  if (v === undefined || v === null) return "—";
  const n = Number(v); if (!Number.isFinite(n)) return String(v);
  if (n >= 1_000_000) return `USD ${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `USD ${(n / 1_000).toFixed(0)}K`;
  return `USD ${n}`;
}
function fmtPct(v: unknown): string { return v === undefined || v === null ? "—" : `${v}%`; }
